import { Component, computed, DestroyRef, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { CwButton, CwDivider, CwTooltip } from '@bbraun/cortex-angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
    FINALIZE_REASON_OPTION_KEYS,
    FINALIZE_REASON_OTHERS_ID
} from '~/app/classes/TherapyJobFormClass';
import { DateFormat } from '~/app/enums/DateFormat';
import { ForceFinalizeTherapyJob } from '~/app/models/TherapyJobV2';
import { LocalizedDatePipe } from '~/app/pipes/LocalizedDate';
import { PersonNamePipe } from '~/app/pipes/PersonName';
import { SafeHtmlPipe } from '~/app/pipes/SafeHtml';
import { Dialog, DialogService } from '~/app/services/dialog.service';
import { DataTypeValidator } from '~/app/utils/DataTypeValidator';
import { DatepickerComponent } from '../datepicker/datepicker.component';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { InputComponent } from '../input/input.component';
import { TextareaComponent } from '../textarea/textarea.component';

const START_DATE_TIME_CONTROL = 'startDateTime';

interface FinalizeReasonOption {
    id: string;
    label: string;
}

interface FinalizeJobForm {
    finalizeReason: FormControl<FinalizeReasonOption>;
    finalizeReasonOther: FormControl<string>;
    infusedVolume: FormControl<string>;
    flowRate: FormControl<string>;
    startDateTime: FormControl<Date>;
    endDateTime: FormControl<Date>;
}

@Component({
    selector: 'gplus-finalize-job-modal',
    imports: [
    TranslateModule,
    ReactiveFormsModule,
    CwButton,
    CwTooltip,
    TextareaComponent,
    InputComponent,
    DatepickerComponent,
    DropdownComponent,
    PersonNamePipe,
    SafeHtmlPipe,
    LocalizedDatePipe,
    CwDivider
],
    templateUrl: './finalize-job-modal.component.html',
    styleUrl: './finalize-job-modal.component.scss'
})
export class FinalizeJobModalComponent {
    private readonly dialogService = inject(DialogService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly translateService = inject(TranslateService);

    public readonly dialog = input<Dialog>();

    public readonly finalizeReasonMaxLength = 500;
    public readonly finalizeReasonOthersId = FINALIZE_REASON_OTHERS_ID;
    public readonly volumeMeasure = this.translateService.instant('UnitMl');
    public readonly flowRateMeasure = this.translateService.instant('UnitMlHr');
    public readonly dateFormat = DateFormat;

    public readonly reasonOptions: Array<FinalizeReasonOption> = FINALIZE_REASON_OPTION_KEYS.map((option) => ({
        id: option.id,
        label: this.translateService.instant(option.translationKey)
    }));

    public readonly form: FormGroup<FinalizeJobForm> = new FormGroup<FinalizeJobForm>({
        finalizeReason: new FormControl(null, [Validators.required]),
        finalizeReasonOther: new FormControl(null, [Validators.maxLength(this.finalizeReasonMaxLength)]),
        infusedVolume: new FormControl(null, [Validators.required, Validators.min(0), DataTypeValidator.isNumber()]),
        flowRate: new FormControl(null, [Validators.min(0), DataTypeValidator.isNumber()]),
        startDateTime: new FormControl(new Date(), [Validators.required]),
        endDateTime: new FormControl(new Date(), [
            Validators.required,
            (control: AbstractControl): ValidationErrors | null => this.validateEndTime(control)
        ])
    });

    private readonly formValue = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });

    public readonly showOtherReason = computed<boolean>(
        () => this.formValue().finalizeReason?.id === FINALIZE_REASON_OTHERS_ID
    );

    constructor() {
        // The end time validity depends on the start control too, so a start change must re-run the check
        this.form.controls.startDateTime.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.form.controls.endDateTime.updateValueAndValidity());

        // "Others" requires the free-text reason to be filled in, and clears stale text otherwise
        effect(() => {
            const otherControl = this.form.controls.finalizeReasonOther;

            if (this.showOtherReason()) {
                otherControl.addValidators(Validators.required);
            } else {
                otherControl.removeValidators(Validators.required);
                otherControl.setValue(null, { emitEvent: false });
            }

            otherControl.updateValueAndValidity({ emitEvent: false });
        });
    }

    public closeDialog(): void {
        this.dialog().confirm.next(false);
        this.dialogService.close();
    }

    public submitChange(): void {
        if (this.form.invalid) {
            return;
        }

        const { finalizeReason, finalizeReasonOther, infusedVolume, flowRate, startDateTime, endDateTime } =
            this.form.getRawValue();
        const reason = finalizeReason.id === FINALIZE_REASON_OTHERS_ID ? finalizeReasonOther : finalizeReason.label;

        const payload: ForceFinalizeTherapyJob = {
            reason,
            infusedVolumeMl: Number(infusedVolume),
            startTime: new Date(startDateTime).toISOString(),
            endTime: new Date(endDateTime).toISOString(),
            flowRateMlPerHour: flowRate ? Number(flowRate) : undefined
        };

        this.dialog().confirm.next(payload);
    }

    private validateEndTime(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null;
        }

        const endTime = new Date(control.value).getTime();

        if (Number.isNaN(endTime)) {
            return null;
        }

        if (endTime > Date.now()) {
            return { endTimeInFuture: true };
        }

        const startValue = control.parent?.get(START_DATE_TIME_CONTROL)?.value as Date;
        const startTime = startValue ? new Date(startValue).getTime() : null;

        if (startTime !== null && !Number.isNaN(startTime) && endTime < startTime) {
            return { endTimeBeforeStart: true };
        }

        return null;
    }
}
